import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { Archive, Check, Copy, RotateCcw, VenetianMask } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { nouns } from "@/components/sheet/nouns";
import { useArrived } from "@/components/sheet/useArrived";
import { loopExamplesFor } from "../constants";
import { demoFor, type DemoLine } from "./demo";
import { braneRun, loopRun, stepAt, type Run } from "./runs";

/*
  The Brane and Loop examples as a process: a window with the address bar the
  real page has, and inside it the steps in the order a person goes through
  them. Over the window, a rail with one segment per step, filling as the run
  goes, and one sentence that says what is happening now; a segment is a
  button that plays the run from that step, and Replay plays it from the start.

  The scenes lie on top of each other in one grid cell, and so do the
  sentences, so nothing moves when either changes. The clock starts when the
  panel first arrives on screen, and the run is a function of it (runs.ts).
  With reduced motion it shows the end of the run, a segment shows the end of
  its step, and there is no replay.
*/

/** The milliseconds into `run`: from when the panel arrived, or from the step last chosen; all of it at once with reduced motion. */
function useRun<F>(run: Run<F>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const arrived = useArrived(ref, 0.4);
  const reduced = useReducedMotion() ?? false;
  const { length, steps } = run;
  // Where the clock last started from, when someone chose it; a fresh object each time, so the same step twice restarts it.
  const [from, setFrom] = useState<{ at: number } | null>(null);
  const [ms, setMs] = useState(reduced ? length : 0);
  useEffect(() => {
    if (reduced) {
      setMs(length);
      return;
    }
    if (!arrived && !from) return;
    const began = performance.now() - (from?.at ?? 0);
    let raf = 0;
    const tick = () => {
      const t = performance.now() - began;
      // The clock moves in 25 ms steps, but its last reading is the end exactly, so the run always finishes.
      setMs(t >= length ? length : Math.floor(t / 25) * 25);
      if (t < length) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [arrived, reduced, length, from]);
  const seek = (i: number) => {
    if (reduced) setMs(i + 1 < steps.length ? steps[i + 1] - 1 : length);
    else setFrom({ at: steps[i] });
  };
  return { ref, ms, length, steps, step: stepAt(steps, ms), reduced, seek };
}

type Clock = ReturnType<typeof useRun>;

/** How much of step `i` has played, 0 to 1. */
function filled(run: Clock, i: number) {
  const begin = run.steps[i];
  const end = run.steps[i + 1] ?? run.length;
  return Math.max(0, Math.min(1, (run.ms - begin) / (end - begin)));
}

function Frame({
  run,
  address,
  label,
  steps,
  children,
}: {
  run: Clock;
  address: string;
  label: string;
  /** One sentence per step of the run, in order. */
  steps: string[];
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const fills = useRef<(HTMLSpanElement | null)[]>([]);
  // The fill is written straight to the DOM: it changes every frame, and a style attribute is not a token.
  useLayoutEffect(() => {
    fills.current.forEach((el, i) => el?.style.setProperty("transform", `scaleX(${filled(run, i)})`));
  });
  return (
    <div ref={run.ref} className="sh-flow">
      <div className="sh-flow__steps">
        <ol className="sh-flow__rail" aria-label={t("landing.sheet.flow.steps", { label })}>
          {steps.map((text, i) => (
            <li key={i}>
              <button
                type="button"
                className="sh-flow__step"
                aria-current={i === run.step ? "step" : undefined}
                aria-label={`${i + 1}. ${text}`}
                onClick={() => run.seek(i)}
              >
                <span className="sh-flow__track">
                  <span
                    ref={(el) => {
                      fills.current[i] = el;
                    }}
                    className="sh-flow__fill"
                  />
                </span>
              </button>
            </li>
          ))}
        </ol>
        {!run.reduced && (
          <button type="button" className="sh-tab sh-flow__replay" onClick={() => run.seek(0)}>
            <Icon glyph={RotateCcw} scale="inline" />
            <span className="sh-flow__replay-label">{t("landing.sheet.flow.replay")}</span>
          </button>
        )}
      </div>
      <div className="sh-flow__captions">
        {steps.map((text, i) => (
          <p key={i} className="sh-flow__caption" data-on={i === run.step || undefined} aria-hidden={i !== run.step}>
            <span className="sh-flow__num">{i + 1}</span>
            <span>{nouns(text)}</span>
          </p>
        ))}
      </div>
      <div className="sh-flow__window" role="group" aria-label={label}>
        <div className="sh-flow__bar" aria-hidden="true">
          <span className="sh-flow__address">{address}</span>
        </div>
        <div className="sh-flow__body">{children}</div>
      </div>
    </div>
  );
}

function Scene({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <div className="sh-flow__scene" data-on={on || undefined} aria-hidden={!on}>
      {children}
    </div>
  );
}

/** Text being typed, with its caret while it is. */
function Typed({ text, typing, placeholder }: { text: string; typing: boolean; placeholder: string }) {
  return (
    <span className="sh-flow__typed">
      {text || <span className="sh-flow__hint">{placeholder}</span>}
      {typing && <span className="sh-flow__caret" aria-hidden="true" />}
    </span>
  );
}

function FlowLines({ lines, shown }: { lines: DemoLine[]; shown: number }) {
  return (
    <ol className="sh-lines">
      {lines.map((line, i) => {
        const showWho = !line.own && lines[i - 1]?.who !== line.who;
        return (
          <li key={i} className={cn("sh-line sh-flow__line", line.own && "is-own", i < shown && "is-shown")}>
            {showWho && <span className="sh-line__who">{line.who}</span>}
            <span className="sh-line__text">{line.text}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** The handle the example Brane has: the shape of a generated one (CONTEXT.md, Handle). */
const HANDLE = "brave-yak-27";
const LINK = `astring.app/b/${HANDLE}`;

const BRANE_STEPS = ["make", "share", "write", "string"] as const;
const LOOP_STEPS = ["make", "room", "archive"] as const;

export function BraneFlow() {
  const { t, i18n } = useTranslation();
  const demo = demoFor(i18n.language).brane;
  const message = demo.lines[0].text;
  const script = braneRun(demo.name.length, demo.bio.length, message.length, demo.lines.length);
  const run = useRun(script);
  const f = script.at(run.ms);
  const address = {
    make: "astring.app/y/settings/branes",
    card: LINK,
    write: `astring.app/y/b/${HANDLE}`,
    chat: "astring.app/y/strings",
  }[f.scene];
  return (
    <Frame
      run={run}
      address={address}
      label={t("landing.brane.panel")}
      steps={BRANE_STEPS.map((s) => t(`landing.sheet.flow.brane.${s}`))}
    >
      {/* Settings, Branes: the card being made, then its link, copied to share. */}
      <Scene on={f.scene === "make"}>
        <div className="sh-flow__compose">
          <div className="sh-flow__titlebar">
            <h3 className="sh-flow__title">{t("y.settings.branes.emptyTitle")}</h3>
            <span className={cn("sh-flow__plate sh-flow__plate--small", f.saved && "is-pressed")}>{t("y.settings.branes.save")}</span>
          </div>
          <div className="sh-flow__fields">
            <span aria-hidden="true" className="sh-flow__emoji sh-flow__emoji--well">
              {demo.emoji}
            </span>
            <div>
              <p className="sh-flow__label">{t("y.settings.branes.name")}</p>
              <div className="sh-flow__textarea sh-flow__textarea--line">
                <Typed
                  text={demo.name.slice(0, f.name)}
                  typing={f.name > 0 && f.name < demo.name.length}
                  placeholder={t("y.settings.branes.namePlaceholder")}
                />
              </div>
            </div>
          </div>
          <div>
            <p className="sh-flow__label">{t("y.settings.branes.bio")}</p>
            <div className="sh-flow__textarea sh-flow__textarea--short">
              <Typed
                text={demo.bio.slice(0, f.bio)}
                typing={f.bio > 0 && f.bio < demo.bio.length}
                placeholder={t("y.settings.branes.bioPlaceholder")}
              />
            </div>
          </div>
          {/* The link: its handle is made on Save, and copying it is how a Brane is shared. */}
          <div className="sh-flow__link" data-on={f.saved || undefined}>
            <p className="sh-flow__url">{f.saved ? LINK : t("y.settings.branes.handleOnSave")}</p>
            <span className={cn("sh-flow__plate sh-flow__plate--small sh-flow__plate--copy", f.copied && "is-done")}>
              <Icon glyph={f.copied ? Check : Copy} scale="inline" />
              {f.copied ? t("y.settings.branes.copied") : t("y.settings.branes.copy")}
            </span>
          </div>
        </div>
      </Scene>
      {/* What /b/<handle> shows whoever opens the link: the card on the String's field, and Send. */}
      <Scene on={f.scene === "card"}>
        <div className="sh-flow__field">
          <article className="sh-flow__card">
            <div className="sh-flow__head">
              <span aria-hidden="true" className="sh-flow__emoji">
                {demo.emoji}
              </span>
              <h3 className="sh-flow__name">{demo.name}</h3>
            </div>
            <p className="sh-flow__bio">{demo.bio}</p>
            <p className="sh-flow__verified">
              <span>{t("loops.demo.scope")}</span>
              <span className="sh-flow__mark">
                <Icon glyph={Check} scale="inline" />
                {t("brane.verified")}
              </span>
            </p>
            <span className={cn("sh-flow__plate", f.pressed && "is-pressed")}>{t("brane.send")}</span>
            <p className="sh-flow__note">
              <Icon glyph={VenetianMask} scale="field" />
              <span>
                {t("brane.anonymous")} {t("brane.verifiedOnly")}
              </span>
            </p>
          </article>
        </div>
      </Scene>
      {/* The composer: the message typed, then sent. */}
      <Scene on={f.scene === "write"}>
        <div className="sh-flow__compose">
          <p className="sh-flow__label">{t("y.brane.form")}</p>
          <p className="sh-flow__who">
            <span aria-hidden="true">{demo.emoji}</span> {demo.name}
          </p>
          <div className="sh-flow__textarea">
            <Typed text={message.slice(0, f.typed)} typing={f.typed < message.length} placeholder={t("y.brane.placeholder")} />
          </div>
          <span className={cn("sh-flow__plate sh-flow__plate--send", f.pressed && "is-pressed")}>{t("y.brane.send")}</span>
        </div>
      </Scene>
      {/* The String it opened, as its owner sees it. */}
      <Scene on={f.scene === "chat"}>
        <div className="sh-flow__chat">
          <header className="sh-panel__head">
            <h3 className="sh-panel__name">{demo.them}</h3>
            <p className="sh-panel__meta">
              <span>
                <em>{t("rooms.string.name")}</em>
              </span>
              <span>{t("landing.brane.opened")}</span>
              <span className="sh-panel__mood">
                {demo.emoji} {demo.name}
              </span>
            </p>
          </header>
          <FlowLines lines={demo.lines} shown={f.lines} />
          <footer className="sh-panel__foot">{nouns(t("landing.brane.foot"))}</footer>
        </div>
      </Scene>
    </Frame>
  );
}

export function LoopFlow() {
  const { t, i18n } = useTranslation();
  const loop = loopExamplesFor(i18n.language)[0];
  const lines: DemoLine[] = loop.messages.map((m) => ({
    who: m.emoji ? `${m.emoji} ${m.sender}` : m.sender,
    text: m.text,
    own: m.own,
  }));
  const script = loopRun(loop.topic.length, lines.length);
  const run = useRun(script);
  const f = script.at(run.ms);
  return (
    <Frame
      run={run}
      address={f.scene === "create" ? "astring.app/y/loops/new" : "astring.app/y/loops"}
      label={t("landing.loop.panel")}
      steps={LOOP_STEPS.map((s) => t(`landing.sheet.flow.loop.${s}`))}
    >
      {/* The Loop being made: its topic, and whether it is anonymous. */}
      <Scene on={f.scene === "create"}>
        <div className="sh-flow__compose">
          <div className="sh-flow__titlebar">
            <h3 className="sh-flow__title">{t("y.loops.create.title")}</h3>
            <span className={cn("sh-flow__plate sh-flow__plate--small", f.pressed && "is-pressed")}>{t("y.loops.create.create")}</span>
          </div>
          <div>
            <p className="sh-flow__label">{t("y.loops.create.topicLabel")}</p>
            <div className="sh-flow__textarea sh-flow__textarea--line">
              <Typed text={loop.topic.slice(0, f.typed)} typing={f.typed < loop.topic.length} placeholder={t("y.loops.create.topicPlaceholder")} />
            </div>
            <p className="sh-flow__sub">{t("y.loops.create.topicHint")}</p>
          </div>
          <div>
            <p className="sh-flow__label">{t("y.loops.create.descriptionLabel")}</p>
            <div className="sh-flow__textarea sh-flow__textarea--short">
              <span className="sh-flow__hint">{t("y.loops.create.descriptionPlaceholder")}</span>
            </div>
            <p className="sh-flow__sub">{t("y.loops.create.descriptionHint")}</p>
          </div>
          <div className="sh-flow__toggle">
            <div>
              <p className="sh-flow__who">{t("y.loops.create.anonymousLabel")}</p>
              <p className="sh-flow__sub">{t("y.loops.create.anonymousHint")}</p>
            </div>
            <span className="sh-flow__switch" data-on={f.anonymous || undefined} aria-hidden="true" />
          </div>
        </div>
      </Scene>
      {/* The room it opened, other people writing in it, and then gone quiet: archived, still there to read. */}
      <Scene on={f.scene === "room"}>
        <div className={cn("sh-flow__chat", f.archived && "is-archived")}>
          <header className="sh-panel__head">
            <h3 className="sh-panel__name">{loop.topic}</h3>
            <p className="sh-panel__meta">
              <span>
                <em>{t("rooms.loop.name")}</em>
              </span>
              <span>{t("loops.demo.scope")}</span>
              <span className="sh-panel__mood">{f.archived ? t("y.loops.archived") : t("loops.demo.live")}</span>
            </p>
          </header>
          <FlowLines lines={lines} shown={f.lines} />
          <footer className="sh-panel__foot sh-flow__archived" data-on={f.archived || undefined}>
            <Icon glyph={Archive} scale="inline" />
            <span>{nouns(t("y.loops.archivedTitle"))}</span>
          </footer>
        </div>
      </Scene>
    </Frame>
  );
}
